import { Component, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable, of } from 'rxjs';
import {
  concatMap, map, take, tap
} from 'rxjs/operators';

import { htmlEscapeDecode } from 'app/utils/escape-html';
import {
  AppCustomPropertyInputArbonia as CustomPropertyInputArbonia,
  RevisionsStatus,
  QuoteCustomPropertyKeyType as CustomPropKey,
  Offer
} from 'graphql/models/extended';
import { Account, Branch, ContactPerson, Customer } from 'graphql/models/generated';
import {
  OfferPreview, OfferPreviewSettings
} from 'models/offer-preview-settings';
import { DropdownItem } from 'models/dropdown.model';
import { SendOfferEmailSettings } from 'models/offer-send-mail.model';
import { OfferBase } from 'models/offer.model';
import { AccountService } from 'shared/services/account.service';
import { OfferService } from 'shared/services/offer.service';
import { PricingService } from 'shared/services/pricing.service';
import { StoreService } from 'shared/services/store.service';

import { OfferPreviewSendComponent } from '../offer-preview-send/offer-preview-send.component';
import { analyticsTrackQuoteProfit } from 'app/utils/analytics-utils'
import { extractDocVersionString } from 'app/utils/document-version';

@Component({
  selector: 'app-offer-preview-send-modal',
  templateUrl: './offer-preview-send-modal.component.html',
  styleUrls: ['./offer-preview-send-modal.component.scss']
})
export class OfferPreviewSendModalComponent {
  @ViewChild(OfferPreviewSendComponent) offerPreviewSendComponent!: OfferPreviewSendComponent;

  get isDealer(): boolean {
    return this.offerPreviewSendComponent.isDealer;
  }

  get form(): UntypedFormGroup {
    return this.offerPreviewSendComponent.form;
  }

  get rfp(): any {
    return this.offerPreviewSendComponent.rfp;
  }

  get offer(): OfferBase {
    return this.offerPreviewSendComponent.offer;
  }

  get currentAccount(): Account {
    return this.offerPreviewSendComponent.currentAccount;
  }

  get currentBranch(): Branch {
    return this.offerPreviewSendComponent.currentBranch;
  }

  get contactPerson(): ContactPerson {
    return this.offerPreviewSendComponent.contactPerson;
  }

  get customer(): Customer {
    return this.offerPreviewSendComponent.customer;
  }

  get dropdownDealers(): DropdownItem[] {
    return this.offerPreviewSendComponent.dropdownData.dealers;
  }

  get offerPreviewSettings(): OfferPreviewSettings {
    return this.offerPreviewSendComponent.offerPreviewSettings;
  }

  constructor(
    private accountService: AccountService,
    private offerService: OfferService,
    private pricingService: PricingService,
    private storeService: StoreService,
    private translateService: TranslateService
  ) { }

  onSubmit(): Observable<any> {
    this.offerPreviewSendComponent.setPdfReady(false);

    const offerDocumentId = { documentId: this.offer.documentId, revision: this.offer.revision };
    const offerPreviewSettings = { ...this.offerPreviewSettings, temporary: false, pdfSrc: undefined };
    const emailControlValue = this.form.controls.email.value;
    let sendTo = this.getMailRecipientsList(emailControlValue);
    let accountEmail = '';

    if (Array.isArray(emailControlValue)){
      accountEmail = emailControlValue[0];
    } else {
      accountEmail = emailControlValue;
    }

    const titleToUse = this.offerService.getCurrentOfferTitle(this.offer as Offer, this.isDealer);
    return combineLatest([
      this.getMailSubject(titleToUse),
      this.accountService.getAccountBasic(accountEmail),
      this.offerService.createOfferPreview(offerDocumentId, offerPreviewSettings)
    ]).pipe(
      concatMap(([mailSubject, recipientAccount]: [string, Account, OfferPreview]) => {
        const textString = this.form.controls.text.value as string;
        const text = htmlEscapeDecode(textString);
        const offerLinkText = !this.isDealer
          ? this.translateService.instant('OFFER.EMAIL.GO_TO_OFFER', { offerLink: window.location.href })
          : '';

        const mailSettings: SendOfferEmailSettings = {
          attachPdfPreview: true,
          bccToCurrentUser: true,
          subject: mailSubject,
          recipient: sendTo,
          text,
          sendAdditionalEmail: true,
          offerLink: window.location.href,
          offerLinkText,
          title: titleToUse
        };

        const sendOffer$ = this.offerService.sendOfferEmail(offerDocumentId, mailSettings);
        const sendUserId =  this.isDealer ? '' : (recipientAccount?.id || '');
        let targetBranchId: string | undefined = (!this.rfp && !sendUserId ? this.offer.customerId : '');

        if (this.isDealer) {
          return sendOffer$.pipe(
            concatMap(() => this.offerService.setOfferStatus(this.offer, RevisionsStatus.SENT)),
            concatMap(() => this.offerService.updateOfferTitle(
              sendUserId,
              { documentId: this.offer.documentId, revision: this.offer.revision },
              targetBranchId
            ).pipe(
              tap((response) => {
                this.storeService.setOfferUpdate({
                  dealerOfferTitle: response.data.updateOfferTitle.dealerTitle,
                  manufacturerOfferTitle: response.data.updateOfferTitle.manufacturerTitle,
                })
              })
            )),
            concatMap(() => this.offerService.lockOffer(this.offer).pipe(
              tap(() => {
                if(this.offer.dealerBuyPrice) {
                  analyticsTrackQuoteProfit(
                    'quote|workflow-email|' + extractDocVersionString(this.offer),
                    this.offer.dealerBuyPrice
                  );
                }
              })
            ))
          )
        }


        const customProperties: CustomPropertyInputArbonia[] = [
          { key: CustomPropKey.SendSubject, value: mailSubject, propertyType: 'String' },
          { key: CustomPropKey.SendUserId, value: sendUserId, propertyType: 'String' },
          { key: CustomPropKey.SendMessage, value: text, propertyType: 'String' }
        ];

        return sendOffer$.pipe(
          concatMap(() => this.pricingService.setOfferAutoUpdatePrices(this.offer as Offer, true)),
          concatMap(() => this.offerService.setOfferStatus(this.offer, RevisionsStatus.ANSWERED_RFP)),
          concatMap(() => this.offerService.updateOffer(this.offer, customProperties)),
          concatMap(() => this.offerService.updateOfferTitle(
            sendUserId,
            { documentId: this.offer.documentId, revision: this.offer.revision },
            targetBranchId
          ).pipe(
            tap((response) => {
              this.storeService.setOfferUpdate({
                dealerOfferTitle: response.data.updateOfferTitle.dealerTitle,
                manufacturerOfferTitle: response.data.updateOfferTitle.manufacturerTitle,
              })
            })
          )),
          concatMap(() => this.offerService.lockOffer(this.offer))
        );
      }),
      take(1)
    );
  }

  getMailSubject(offerTitle?: string): Observable<string> {
    let manufacturer = '';
    let translatedMailSubject$ = of('');

    const obs$ = this.storeService.getAccountManufacturer().pipe(
      tap((name: string) => {
        manufacturer = this.translateService.instant(`GENERAL.MANUFACTURER.${name.toUpperCase()}`);
      })
    );

    if (!this.rfp) {
      translatedMailSubject$ = obs$.pipe(
        concatMap(() => this.translateService.get(
          'MODALS.OFFER_PREVIEW.SEND.SUBJECT',
          { manufacturer, offerTitle: offerTitle ?? this.offer.title }
        ))
      );
    } else {
      translatedMailSubject$ = obs$.pipe(
        concatMap(() => this.translateService.get(
          'MODALS.OFFER_PREVIEW.SEND.SUBJECT_RFP',
          { manufacturer, offerTitle: offerTitle ?? this.offer.title, rfpTitle: this.rfp.title }
        ))
      );
    }

    return translatedMailSubject$;
  }

  getMailRecipientsList(emailControlValue: string | string[]): string {
    let result: string[] = [];
    const mailList = (Array.isArray(emailControlValue) ? emailControlValue : [emailControlValue]);

    if (this.isDealer) {
      const offerContactPerson = this.contactPerson;
      const offerCustomer = this.customer;
      result = mailList.map((mail) => {
        if (offerContactPerson && offerContactPerson?.email === mail) {
          return (
            offerContactPerson.firstName && offerContactPerson.lastName
              ? `"${offerContactPerson.firstName} ${offerContactPerson.lastName}" <${mail}>`
              : mail
          );
        }

        if (offerCustomer && offerCustomer?.email === mail) {
          return (offerCustomer.name ? `"${offerCustomer.name}" <${mail}>` : mail);
        }

        return mail;
      });
    } else {
      if (this.rfp) {
        result = mailList.map((mail) => {
          if (this.rfp.snkModifier && this.rfp.snkModifier.name && this.rfp.snkModifier.username === mail) {
            return `"${this.rfp.snkModifier.name}" <${mail}>`;
          }

          return mail;
        });
      } else {
        result = mailList.map((mail) => {
          const dealerData = this.dropdownDealers.find((d) => d.value === mail)
          if (dealerData) {
            return `"${dealerData.title}" <${mail}>`;
          }

          return mail;
        });
      }
    }

    return result.join(',');
  }

  onClose(): void {
    // check and disable pricing calculation
    const priceCalculationObs$ = this.storeService.getOfferDisablePriceCalculation();
    const autoUpdatePrices = this.offer?.autoUpdatePrices;

    priceCalculationObs$.pipe(
      map((disablePricing: boolean) => {
        if (disablePricing && autoUpdatePrices && !this.offer.locked) {
          this.pricingService.turnOffCurrentOfferPricing().subscribe();
        }
      })
    );
  }
}
